/* eslint-disable max-lines */
// deps
import React, { useCallback, useContext, useState, memo, useEffect, useMemo } from "react";

// internal deps
import { format, getCloudinaryUrl, isEmpty } from "@swiggy-private/js-utils";
import { FullWidthButton } from "@swiggy-private/dls-web-core/dist/components/FullWidthButton";
import { Image } from "@swiggy-private/dls-web-core/dist/components/Image";
import { Text } from "@swiggy-private/dls-web-core/dist/components/Text";
import { Textarea } from "@swiggy-private/dls-web-core/dist/components/Textarea";
import { TextField } from "@swiggy-private/dls-web-core/dist/components/TextField";
import { TextFieldTypes } from "@swiggy-private/dls-web-core/dist/components/TextField/types";
import {
    AnalyticsEventContext,
    RouterContext,
    SwiggyHeadersContext,
} from "@swiggy-private/swiggy-money-context/src";

// components
import { GrinningEmoji, HeartEmoji, SmilingEmoji, SmilingHeartEmoji } from "../assets";
import Header from "../components/Header";

// helpers

// constants
import {
    GIFT_CARD_SELECTION_TEST_IDS,
    HOW_IT_WORKS_IMAGE_URL,
    SM_MONEY_PATHS,
    SWIGGY_MONEY_GIFT_CARD_FAQ_URL,
} from "../constants";
import {
    MESSAGE_CONTAINER_INPUT_TITLE,
    AMOUNT_SELECTION_WIDGET_TEXTS,
    FAQ_CTA_TEXT,
    OTHER_AMOUNT,
} from "../constants/gift-card";
import {
    getAmountBoxHintText,
    getDefaultSelectedOption,
    getDiscountedValue,
    getMessageFromSessionData,
    isAmountValid,
    isPredefinedAmountSelected,
} from "../helpers/giftcard";

import { AmountOption } from "../interfaces";
import { AmountBox } from "./components";
import ButtonChild from "./components/ButtonChild";
import {
    AmountBoxes,
    AmountContainer,
    AmountSelectionWidget,
    ButtonContainer,
    Container,
    ContentContainer,
    DiscountSubtitleStyledText,
    FaqButtonStyledButton,
    FaqHeaderContainer,
    HeaderContainer,
    HowItWorksImageContainer,
    ImageContainer,
    MessageContainer,
    MessageTitleStyledText,
} from "./styles";

interface ErrorState {
    amount: boolean;
}

interface GiftCardSelectionProps {
    amountOptions: AmountOption[];
    discount?: number;
    discountMinimumValue?: number;
    onBackClick: VoidFunction;
    cardBannerImageUrl: string;
    defaultMessagePlaceholder: string;
    webNavigateSdk?: (url: string, openAsExternal: boolean, showBack?: boolean) => void;
}

const GiftCardSelection = ({
    cardBannerImageUrl,
    amountOptions,
    discount,
    discountMinimumValue,
    onBackClick,
    defaultMessagePlaceholder,
    webNavigateSdk,
}: GiftCardSelectionProps) => {
    const { router } = useContext(RouterContext);
    const { swiggyHeaders } = useContext(SwiggyHeadersContext);
    const {
        impressionEvent,
        clickEvent,
        impressionSendGiftVoucher,
        clickProceedToPay,
        impressionGiftCardFormError,
        clickFaqOnVoucherSelection,
        clickBackButtonOnVoucherSelection,
        clickMessageBox,
        clickAmount,
    } = useContext(AnalyticsEventContext);

    const onBack = useCallback(() => {
        clickBackButtonOnVoucherSelection?.(clickEvent);
        onBackClick();
    }, [clickBackButtonOnVoucherSelection, clickEvent, onBackClick]);

    const preSelectedAmountOption = getDefaultSelectedOption(amountOptions);
    const isPredefinedSelection = isPredefinedAmountSelected(
        amountOptions,
        preSelectedAmountOption
    );

    // Transform amount options
    const transformedAmountOptions = useMemo(() => {
        amountOptions.forEach((option, index) => {
            if (index == 0) option.emojiNode = <SmilingEmoji />;
            if (index == 1) option.emojiNode = <GrinningEmoji />;
            if (index == 2) option.emojiNode = <SmilingHeartEmoji />;
        });

        const customInputOption = amountOptions.findIndex((option) => option.id == 4);
        if (customInputOption == -1) {
            amountOptions.push({
                id: 4,
                amount: OTHER_AMOUNT,
                preSelected: false,
                text: "Custom",
                emojiNode: <HeartEmoji />,
            });
        }
        return amountOptions;
    }, [amountOptions]);

    const [isCustomSelected, setIsCustomSelected] = useState(
        !isPredefinedSelection && !isEmpty(preSelectedAmountOption)
    );
    const [showCustomInput, setShowCustomInput] = useState(isCustomSelected);
    const defaultMessage = getMessageFromSessionData(defaultMessagePlaceholder);

    const [value, setValue] = useState({
        amount: preSelectedAmountOption,
        message: defaultMessage,
    });

    const [showError, setShowError] = useState<ErrorState>({
        amount: false,
    });

    useEffect(() => {
        if (!isEmpty(swiggyHeaders)) {
            impressionSendGiftVoucher?.(impressionEvent);
        }
    }, [impressionEvent, impressionSendGiftVoucher, swiggyHeaders]);

    useEffect(() => {
        window.addEventListener("popstate", onBack);

        return (): void => {
            window.removeEventListener("popstate", onBack);
        };
    }, [onBack]);

    const resetSelection = useCallback(() => {
        setIsCustomSelected(false);
        setShowCustomInput(false);
        setValue({ ...value, amount: "" });
    }, [value]);

    const triggerClickEvent = useCallback(
        (customSelection: boolean, val: string) => {
            clickAmount?.(clickEvent, {
                amountCategory: customSelection ? "Custom" : val,
                offerCallout: String(discount),
            });
        },
        [clickAmount, clickEvent, discount]
    );

    const updateSelection = useCallback(
        (customSelection: boolean, val: string) => {
            setIsCustomSelected(customSelection);
            setShowCustomInput(customSelection);
            setValue({ ...value, amount: customSelection ? "" : val });
        },
        [value]
    );

    const amountSelection = useCallback(
        (val: string) => {
            const customSelection = val == String(OTHER_AMOUNT);

            if (isCustomSelected && customSelection) {
                resetSelection();
                return;
            }

            triggerClickEvent(customSelection, val);
            updateSelection(customSelection, val);
        },
        [isCustomSelected, resetSelection, triggerClickEvent, updateSelection]
    );

    const handleOnChange = useCallback(
        (key: string) => {
            return (val: string) => {
                if (key === "message" && val.includes("\n")) {
                    val = val.replace("\n", "");
                }

                setValue({
                    ...value,
                    [key]: val,
                });
                setShowError({
                    ...showError,
                    [key]: false,
                });
            };
        },
        [showError, value]
    );

    const handleSubmit = useCallback(() => {
        if (!isAmountValid(value.amount.trim())) {
            const errorObj: ErrorState = {
                amount: showError.amount,
            };

            errorObj.amount = true;
            setShowError(errorObj);
            impressionGiftCardFormError?.(impressionEvent, errorObj);
            return;
        }

        sessionStorage.setItem(
            "gift-details",
            JSON.stringify({
                receiverMeta: {
                    message: value.message.trim(),
                    imageurl: cardBannerImageUrl,
                },
                creditType: "Voucher",
                originalAmount: value.amount.trim(),
                discountedAmount: String(
                    getDiscountedValue(value.amount.trim(), discount, discountMinimumValue)
                ),
            })
        );

        const giftDetails = JSON.parse(sessionStorage.getItem("gift-details") as string);
        clickProceedToPay?.(clickEvent, {
            originalAmount: giftDetails.originalAmount,
            discountedAmount: giftDetails.discountedAmount,
            message: value.message.trim(),
        });

        router.push(SM_MONEY_PATHS.GIFT_CARD_PAYMENTS);
    }, [
        cardBannerImageUrl,
        clickEvent,
        clickProceedToPay,
        discount,
        discountMinimumValue,
        impressionEvent,
        impressionGiftCardFormError,
        router,
        showError.amount,
        value.amount,
        value.message,
    ]);

    const amountDetails = {
        value: value.amount,
        type: TextFieldTypes.NUMBER,
        name: "amount",
        label: "Enter gift voucher amount*",
        onChange: handleOnChange("amount"),
        errorText: showError.amount ? "Amount should be between ₹50 and ₹10000" : "",
        hintText: getAmountBoxHintText({
            amountError: showError.amount,
            amount: value.amount,
            discount,
            discountMinimumValue,
        }),
        validFormat: "^[0-9]*$",
    };

    useEffect(() => {
        if (!value.amount) return;

        if (!isAmountValid(value.amount)) {
            setShowError({ amount: true });
            impressionGiftCardFormError?.(impressionEvent, {
                errorMessage: amountDetails.errorText,
            });
        } else {
            setShowError({ amount: false });
        }
    }, [amountDetails.errorText, impressionEvent, impressionGiftCardFormError, value.amount]);

    const navigateToFaq = useCallback(() => {
        clickFaqOnVoucherSelection?.(clickEvent);
        webNavigateSdk?.(SWIGGY_MONEY_GIFT_CARD_FAQ_URL, true, true);
    }, [clickEvent, clickFaqOnVoucherSelection, webNavigateSdk]);

    const onFocusMessageField = useCallback(() => {
        clickMessageBox?.(clickEvent);
    }, [clickEvent, clickMessageBox]);

    return (
        <Container>
            <HeaderContainer>
                <Header title="Swiggy gift voucher" onBackClick={onBack} isGiftcardFlow />
                <FaqHeaderContainer data-testid={GIFT_CARD_SELECTION_TEST_IDS.FAQ_BUTTON}>
                    <FaqButtonStyledButton
                        buttonSize="small"
                        color="text_Highest_Emphasis"
                        underline
                        onClick={navigateToFaq}
                    >
                        {FAQ_CTA_TEXT}
                    </FaqButtonStyledButton>
                </FaqHeaderContainer>
            </HeaderContainer>
            <ContentContainer>
                <ImageContainer data-testid={GIFT_CARD_SELECTION_TEST_IDS.MAIN_BANNER_CONTAINER}>
                    <Image src={getCloudinaryUrl(cardBannerImageUrl)} fit="contain" />
                </ImageContainer>

                <AmountSelectionWidget>
                    <Text variant="V2.body1.neutral.medium" color="text_Med_Emphasis">
                        {AMOUNT_SELECTION_WIDGET_TEXTS.TITLE}
                    </Text>

                    {!isEmpty(discount) && (
                        <DiscountSubtitleStyledText
                            variant="V2.body3.neutral.regular"
                            color="text_Med_Emphasis"
                        >
                            {format(
                                AMOUNT_SELECTION_WIDGET_TEXTS.SUBTITLE,
                                discount,
                                discountMinimumValue
                            )}
                        </DiscountSubtitleStyledText>
                    )}

                    <AmountBoxes>
                        {transformedAmountOptions.map((amountOption) => {
                            return (
                                <AmountBox
                                    key={amountOption.id}
                                    value={String(amountOption.amount)}
                                    setAmountSelection={amountSelection}
                                    discount={discount}
                                    discountMinimumValue={discountMinimumValue}
                                    isAmountSelected={value.amount === String(amountOption.amount)}
                                    emojiNode={amountOption.emojiNode}
                                    text={amountOption.text}
                                    isCustomSelected={isCustomSelected}
                                    showMostPopularTag={!!amountOption.mostPopular}
                                />
                            );
                        })}
                    </AmountBoxes>

                    {showCustomInput && (
                        <AmountContainer>
                            <TextField textFieldDetails={amountDetails} />
                        </AmountContainer>
                    )}
                </AmountSelectionWidget>
                <MessageContainer>
                    <MessageTitleStyledText
                        variant="V2.body1.neutral.medium"
                        color="text_Med_Emphasis"
                    >
                        {MESSAGE_CONTAINER_INPUT_TITLE}
                    </MessageTitleStyledText>
                    <Textarea
                        value={value.message}
                        name="message"
                        onFocus={onFocusMessageField}
                        onChange={handleOnChange("message")}
                        inputLimit={50}
                        label=""
                    />
                </MessageContainer>
            </ContentContainer>
            <HowItWorksImageContainer>
                <Image
                    src={getCloudinaryUrl(HOW_IT_WORKS_IMAGE_URL)}
                    alt="How it works"
                    fit="contain"
                />
            </HowItWorksImageContainer>
            <ButtonContainer>
                <FullWidthButton
                    variant="positive"
                    onClick={handleSubmit}
                    isDisabled={isEmpty(value.amount) || showError.amount}
                >
                    <ButtonChild
                        amount={value.amount}
                        discount={discount}
                        minimumDiscountValue={discountMinimumValue}
                        showError={showError.amount}
                    />
                </FullWidthButton>
            </ButtonContainer>
        </Container>
    );
};

export default memo(GiftCardSelection);
